// Script v4 identity grammar. Owns the alphabet, the prefixes, the patterns and
// the generator so that nothing downstream has to restate them — a second copy
// of this alphabet is a second definition of what a valid script is.
import { customAlphabet } from "nanoid";
export const V4_ID_ALPHABET = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
export const V4_ID_RANDOM_LENGTH = 6;
export const V4_ID_PREFIXES = {
    episode: "ep",
    scene: "scn",
    action: "ac",
    actor: "act",
    location: "loc",
    prop: "prp",
    state: "st",
};
const randomSuffix = customAlphabet(V4_ID_ALPHABET, V4_ID_RANDOM_LENGTH);
const alphabetClass = `[${V4_ID_ALPHABET}]`;
export const V4_ID_PATTERNS = Object.fromEntries(Object.entries(V4_ID_PREFIXES).map(([kind, prefix]) => [
    kind,
    new RegExp(`^${prefix}${alphabetClass}{${V4_ID_RANDOM_LENGTH}}$`),
]));
export function isV4Id(kind, value) {
    return typeof value === "string" && V4_ID_PATTERNS[kind].test(value);
}
/** Marks the one failure `generateV4Id` raises, so callers can map it without
 *  catching everything the function might ever throw. */
export const ID_SPACE_EXHAUSTED = "ID_SPACE_EXHAUSTED";
export function generateV4Id(kind, existing = new Set(), generateSuffix = randomSuffix) {
    const prefix = V4_ID_PREFIXES[kind];
    for (let attempt = 0; attempt < 128; attempt++) {
        const candidate = `${prefix}${generateSuffix()}`;
        if (!existing.has(candidate))
            return candidate;
    }
    throw Object.assign(new Error(`Could not generate an unused ${kind} ID after 128 attempts.`), { code: ID_SPACE_EXHAUSTED });
}
export function generateV4Ids(kind, count, existing = new Set()) {
    if (!Number.isSafeInteger(count) || count < 1) {
        throw new Error(`count must be a positive integer (got ${count}).`);
    }
    const seen = new Set(existing);
    const ids = [];
    while (ids.length < count) {
        const id = generateV4Id(kind, seen);
        seen.add(id);
        ids.push(id);
    }
    return ids;
}
